/**
 * MatchupCenter - head-to-head matchup with cosmetic live-scoring ticker
 * and coupled per-player projection delta ticks.
 *
 * IMPORTANT: per founder spec - delta ticks fire IN COUPLED RESPONSE to the
 * ticker's play-by-play events. They never fire on independent timers.
 * Magnitude range ±0.2 to ±2.5. This is the animation infrastructure;
 * Phase 6 swaps in real deltas via live data.
 */
import { useEffect, useState, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../contexts/ThemeContext";
import { useSeason } from "../contexts/SeasonContext";
import { authHeaders } from "../contexts/AuthContext";
import SeasonShell from "../components/season/SeasonShell";
import ManagerName from "../components/ManagerName";
import { PlayerHeadshot } from "../components/PlayerHeadshot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cosmetic play-by-play templates (Phase 1 - Phase 6 swaps in real plays)
const PLAY_TEMPLATES = [
  "{name} caught a {yds}-yard pass.",
  "{name} broke free for {yds} yards.",
  "{name} gained {yds} on the ground.",
  "{name} reeled in a {yds}-yard reception.",
  "{name} dropped a deep ball - incomplete.",
  "{name} ran for a tough {yds}.",
  "{name} just barely snagged a short pass.",
  "{name} took a hit but held on for {yds}.",
  "{name} found a hole - {yds} pickup.",
  "{name} stiff-armed his way to {yds}.",
];

const POS_TICKS = { QB: 1, RB: 1, WR: 1, TE: 1 };  // K and DEF have less variance


/** Generate a single cosmetic play. Couples to a player; returns the delta. */
function makePlay(player, week, seedSeq) {
  // Magnitude in [0.2, 2.5], sign weighted slightly positive (since most plays gain yards)
  const magnitudeSeed = (week * 1000 + seedSeq) >>> 0;
  const rand = (s) => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  };
  const r = rand(magnitudeSeed);
  const r2 = rand(magnitudeSeed + 1);
  const sign = r < 0.65 ? 1 : -1;  // 65% positive plays
  const magnitude = 0.2 + r2 * 2.3;  // 0.2 .. 2.5
  const delta = sign * magnitude;

  // Cosmetic narrative - pick yards based on magnitude
  const yds = Math.round(2 + Math.abs(delta) * 6 + rand(magnitudeSeed + 2) * 6);
  const tplIdx = Math.floor(rand(magnitudeSeed + 3) * PLAY_TEMPLATES.length);
  const tpl = PLAY_TEMPLATES[tplIdx];
  const text = tpl.replace("{name}", player.name).replace("{yds}", yds);

  return { delta: Math.round(delta * 10) / 10, text, playerId: player.id };
}


export default function MatchupCenter() {
  const { id: leagueId, week: weekParam } = useParams();
  const { isDark } = useTheme();
  const { actingAsTeamId } = useSeason();
  const [week, setWeek] = useState(parseInt(weekParam, 10) || 1);
  const [seasonInfo, setSeasonInfo] = useState(null);
  const [matchups, setMatchups] = useState([]);
  const [selectedMid, setSelectedMid] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tickerLine, setTickerLine] = useState(null);
  const [deltaTicks, setDeltaTicks] = useState({});  // playerId -> {value, key}
  const seedSeqRef = useRef(0);

  useEffect(() => {
    axios.get(`${API}/season/${leagueId}`, { headers: authHeaders() })
      .then((r) => {
        setSeasonInfo(r.data);
        if (!weekParam) setWeek(r.data.current_week);
      });
  }, [leagueId, weekParam]);

  useEffect(() => {
    if (!leagueId || !week) return;
    axios.get(`${API}/matchups/league/${leagueId}/week/${week}`, { headers: authHeaders() })
      .then((r) => {
        setMatchups(r.data || []);
        // Pick a matchup involving the acting team if possible, else first
        const target = (r.data || []).find((m) =>
          m.home.team_id === actingAsTeamId || m.away.team_id === actingAsTeamId
        ) || (r.data || [])[0];
        setSelectedMid(target?.matchup_id || null);
      });
  }, [leagueId, week, actingAsTeamId]);

  useEffect(() => {
    if (!selectedMid) return;
    axios.get(`${API}/matchups/${selectedMid}`, { headers: authHeaders() })
      .then((r) => setDetail(r.data));
  }, [selectedMid]);

  // ── Live-scoring ticker (cosmetic; only for current week + scheduled matchups) ──
  const isCurrentWeek = seasonInfo && seasonInfo.current_week === week;
  const isLive = detail && detail.status !== "FINAL" && isCurrentWeek;

  useEffect(() => {
    if (!isLive || !detail) return;
    const allStarters = [
      ...detail.home.lineup.filter((s) => s.is_starter && s.player),
      ...detail.away.lineup.filter((s) => s.is_starter && s.player),
    ];
    if (allStarters.length === 0) return;

    const tick = () => {
      seedSeqRef.current += 1;
      const slot = allStarters[seedSeqRef.current % allStarters.length];
      const play = makePlay(slot.player, week, seedSeqRef.current);
      setTickerLine({ ...play, ts: Date.now() });
      setDeltaTicks((prev) => ({
        ...prev,
        [play.playerId]: { value: play.delta, key: Date.now() + Math.random() },
      }));
      // Clear delta tick after animation
      setTimeout(() => {
        setDeltaTicks((prev) => {
          const next = { ...prev };
          if (next[play.playerId]?.key) delete next[play.playerId];
          return next;
        });
      }, 1500);
    };

    const interval = setInterval(tick, 4500);
    tick();  // immediate first
    return () => clearInterval(interval);
  }, [isLive, detail, week]);

  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";

  if (!seasonInfo) return <SeasonShell><div className="py-20 text-center font-display font-black uppercase text-2xl text-[#FF6B00]">Loading…</div></SeasonShell>;

  return (
    <SeasonShell>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF6B00] font-display font-bold mb-1">Matchup Center</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">Week {week}</h1>
        </div>
        <select
          value={week}
          onChange={(e) => setWeek(parseInt(e.target.value, 10))}
          className={`px-3 py-2 rounded-lg ${isDark ? "bg-[#252525] text-white" : "bg-white text-black"} border ${cardBorder} text-sm`}
          data-testid="mc-week-select"
        >
          {Array.from({ length: seasonInfo.regular_season_weeks }, (_, i) => i + 1).map((w) => (
            <option key={w} value={w}>Week {w}</option>
          ))}
        </select>
      </div>

      {/* Matchup picker pills */}
      <div className="flex flex-wrap gap-2 mb-5">
        {matchups.map((m) => (
          <button
            key={m.matchup_id}
            onClick={() => setSelectedMid(m.matchup_id)}
            className={`px-3 py-2 rounded-lg text-xs font-display font-bold uppercase tracking-wide transition ${
              selectedMid === m.matchup_id
                ? "bg-[#FF6B00]/15 text-[#FF6B00] ring-1 ring-[#FF6B00]/40"
                : `${muted} hover:${fg} border ${cardBorder}`
            }`}
            data-testid={`mc-matchup-pill-${m.matchup_id}`}
          >
            <span className="inline-flex items-center gap-2">
              <ManagerName name={m.home.team_name} isBot={m.home.is_bot} iconSize={10} testId={`mc-pill-home-${m.matchup_id}`} />
              <span>{m.status === "FINAL" ? `${m.home.score}-${m.away.score}` : "vs"}</span>
              <ManagerName name={m.away.team_name} isBot={m.away.is_bot} iconSize={10} testId={`mc-pill-away-${m.matchup_id}`} />
            </span>
          </button>
        ))}
      </div>

      {detail && (
        <>
          {/* Live ticker bar (cosmetic) */}
          {isLive && (
            <div
              className={`relative mb-5 rounded-2xl ${cardBg} border ${cardBorder} px-4 py-2.5 overflow-hidden`}
              data-testid="mc-ticker"
            >
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 text-[9px] uppercase tracking-widest font-display font-black bg-red-500/20 text-red-400 rounded animate-pulse">
                  LIVE
                </span>
                <AnimatePresence mode="wait">
                  {tickerLine && (
                    <motion.span
                      key={tickerLine.ts}
                      initial={{ opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -12 }}
                      transition={{ duration: 0.25 }}
                      className={`text-sm ${fg}`}
                    >
                      {tickerLine.text}
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}

          {/* Score header */}
          <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-5 mb-5`} data-testid="mc-score-header">
            <div className="grid grid-cols-3 gap-4 items-center">
              <TeamHeader team={detail.home} isDark={isDark} side="left" />
              <div className="text-center">
                <div className={`text-[10px] uppercase tracking-widest ${muted}`}>Total</div>
                <div className="text-2xl sm:text-3xl font-display font-black">
                  {detail.home.score.toFixed(1)} - {detail.away.score.toFixed(1)}
                </div>
                {detail.status !== "FINAL" && (
                  <div className={`text-[10px] uppercase tracking-widest ${muted} mt-1`}>
                    Proj: {detail.home.projected.toFixed(1)} vs {detail.away.projected.toFixed(1)}
                  </div>
                )}
                <div className={`text-[10px] uppercase tracking-widest font-bold mt-1 ${detail.status === "FINAL" ? "text-[#FF6B00]" : "text-[#00C853]"}`}>
                  {detail.status}
                </div>
              </div>
              <TeamHeader team={detail.away} isDark={isDark} side="right" />
            </div>
          </div>

          {/* Lineup row-by-row */}
          <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-3 sm:p-5`} data-testid="mc-lineup">
            <div className="grid grid-cols-[1fr_60px_1fr] gap-2 sm:gap-3">
              {alignSlots(detail.home.lineup, detail.away.lineup).map(([h, a], idx) => (
                <SlotPair
                  key={`${(h?.slot_id || a?.slot_id)}-${idx}`}
                  home={h} away={a}
                  isLive={isLive}
                  deltaTicks={deltaTicks}
                  isDark={isDark}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </SeasonShell>
  );
}


function TeamHeader({ team, isDark, side }) {
  const align = side === "left" ? "text-right" : "text-left";
  return (
    <div className={align}>
      <div className="text-[10px] uppercase tracking-widest text-[#FF6B00] font-display font-bold">{team.record || "-"}</div>
      <div className="font-display font-black uppercase text-base sm:text-xl truncate">
        <ManagerName name={team.team_name} isBot={team.is_bot} iconSize={14} testId={`mc-team-header-${side}`} />
      </div>
    </div>
  );
}


function alignSlots(homeLineup, awayLineup) {
  // Pair starters first by display order; bench separately
  const homeStarters = homeLineup.filter((s) => s.is_starter);
  const awayStarters = awayLineup.filter((s) => s.is_starter);
  const homeBench = homeLineup.filter((s) => !s.is_starter);
  const awayBench = awayLineup.filter((s) => !s.is_starter);
  const pairs = [];
  for (let i = 0; i < Math.max(homeStarters.length, awayStarters.length); i++) {
    pairs.push([homeStarters[i] || null, awayStarters[i] || null]);
  }
  pairs.push([null, null]); // separator marker
  for (let i = 0; i < Math.max(homeBench.length, awayBench.length); i++) {
    pairs.push([homeBench[i] || null, awayBench[i] || null]);
  }
  return pairs;
}


function SlotPair({ home, away, isLive, deltaTicks, isDark }) {
  // Separator
  if (!home && !away) {
    return <div className="col-span-3 text-center text-[10px] uppercase tracking-widest text-[#FF6B00] font-display font-black my-2">- Bench -</div>;
  }
  return (
    <>
      <PlayerCell side="left" slot={home} isLive={isLive} delta={home?.player ? deltaTicks[home.player.id] : null} isDark={isDark} />
      <div className="flex items-center justify-center">
        <span className="text-[10px] uppercase tracking-widest text-[#FF6B00] font-display font-bold">
          {home?.slot_id || away?.slot_id || ""}
        </span>
      </div>
      <PlayerCell side="right" slot={away} isLive={isLive} delta={away?.player ? deltaTicks[away.player.id] : null} isDark={isDark} />
    </>
  );
}


function PlayerCell({ side, slot, isLive, delta, isDark }) {
  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const cellBg = isDark ? "bg-[#252525]" : "bg-[#F5F5F5]";
  const align = side === "left" ? "flex-row-reverse" : "flex-row";
  const textAlign = side === "left" ? "text-right" : "text-left";

  if (!slot) return <div />;
  const p = slot.player;
  const showActual = slot.actual_points !== null && slot.actual_points !== undefined;
  const points = showActual ? slot.actual_points : slot.projected_points;

  return (
    <div className={`${cellBg} rounded-lg p-2 flex ${align} items-center gap-2 relative`} data-testid={p ? `mc-cell-${p.id}` : "mc-cell-empty"}>
      {p ? (
        <>
          <PlayerHeadshot playerId={p.id} position={p.position} size="sm" />
          <div className={`flex-1 min-w-0 ${textAlign}`}>
            <div className="font-display font-bold uppercase text-xs tracking-wide truncate">{p.name}</div>
            <div className={`text-[10px] ${muted}`}>{p.position} · {p.nfl_team}</div>
          </div>
          <div className={`${textAlign} relative`}>
            <div className={`text-[9px] uppercase tracking-widest ${muted}`}>{showActual ? "Pts" : "Proj"}</div>
            <div className="font-display font-black text-sm relative">
              {(points ?? 0).toFixed(1)}
              {/* Delta tick - coupled to ticker plays (NEVER independent) */}
              <AnimatePresence>
                {delta && (
                  <motion.span
                    key={delta.key}
                    initial={{ opacity: 0, y: 0, scale: 0.8 }}
                    animate={{ opacity: 1, y: -16, scale: 1 }}
                    exit={{ opacity: 0, y: -28 }}
                    transition={{ duration: 0.6 }}
                    className={`absolute right-0 -top-1 text-xs font-display font-black pointer-events-none ${
                      delta.value >= 0 ? "text-[#00C853]" : "text-red-400"
                    }`}
                    data-testid="mc-delta-tick"
                  >
                    {delta.value >= 0 ? "+" : ""}{delta.value.toFixed(1)}
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </div>
        </>
      ) : (
        <span className={`text-xs italic ${muted}`}>(empty)</span>
      )}
    </div>
  );
}
